import { Component, computed, inject, OnInit, Output, EventEmitter } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { MaterialModule } from '../../../../../shared/material.module';
import { TabToolbarComponent } from '../tab-toolbar/tab-toolbar.component';
import { HasUnsavedChanges } from '../../../../../guards/unsaved-changes.guard';
import { ConfirmationDialogService } from '../../../../../services/confirmation-dialog.service';
import { Helpers } from '../../../../../utilities/helpers';
import { CompanyProcessIndicationService } from '../../../../../services/companies/company-process-indication.service';
import { CompanyProcessIndicationDto, UpsertProcessIndicationRequest, ProcessIndicationStatusUpdateRequest } from '../../../../../models/api/companyProcessIndicationDto';
import { selectTabYear, selectDefaultYear } from '../../../../../store/selectors/company-form.selectors';
import { setTabYear } from '../../../../../store/actions/company-form.actions';
import { HotToastService } from '@ngxpert/hot-toast';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { ApiErrorResponse } from '../../../../../models/api/apiErrorResponse';
import { ErrorCodes } from '../../../../../models/api/errorCodes';
import { CurrentUserState } from '../../../../../state/current-user.state';
import { AppUserRole } from '../../../../../models/enums/appUserRole.enum';
import { Category } from '../../../../../models/enums/category.enum';
import { ReadOnlyTabDirective } from '../../../../../directives/read-only-tab.directive';
import { AppUserCompanyAccessType } from '../../../../../models/enums/appUserCompanyAccessType.enum';

interface ProcessDomain {
  key: string;
  label: string;
  category: Category;
  checkedField: string;
  checkedDateField: string;
  checkedByNameField: string;
  finishedField: string;
  finishedDateField: string;
  finishedByNameField: string;
}

@Component({
  selector: 'app-process-tab',
  imports: [
    ReactiveFormsModule,
    MaterialModule,
    NgxSpinnerModule,
    TabToolbarComponent,
    ReadOnlyTabDirective
  ],
  templateUrl: './process-tab.component.html',
  styleUrl: './process-tab.component.scss'
})
export class ProcessTabComponent implements OnInit, HasUnsavedChanges {
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private store = inject(Store);
  private processIndicationService = inject(CompanyProcessIndicationService);
  private confirmationDialog = inject(ConfirmationDialogService);
  private toast = inject(HotToastService);
  private spinnerService = inject(NgxSpinnerService);
  private currentUserState = inject(CurrentUserState);

  /** Tab held read-only: the form is disabled by ReadOnlyTabDirective and row actions are hidden. */
  readonly isReadOnly = computed(() =>
    this.currentUserState.isCompanyTabReadOnly(AppUserCompanyAccessType.Process));

  @Output() saved = new EventEmitter<void>();

  selectedYear: string = this.currentUserState.defaultMeasurementYear();
  companyId: string | null = null;
  showYearWarning = false;
  userNames: { [key: string]: string | null } = {};
  isUserRole = false;
  isLeaderRole = false;
  visibleDomains: ProcessDomain[] = [];

  readonly domains: ProcessDomain[] = [
    {
      key: 'audit',
      label: 'Audit',
      category: Category.Audit,
      checkedField: 'isAuditChecked',
      checkedDateField: 'auditCheckedDate',
      checkedByNameField: 'auditCheckedByUserName',
      finishedField: 'isAuditFinished',
      finishedDateField: 'auditFinishedDate',
      finishedByNameField: 'auditFinishedByUserName'
    },
    {
      key: 'corporateGovernance',
      label: 'Corporate Governance',
      category: Category.CorporateGovernance,
      checkedField: 'isCorporateGovernanceChecked',
      checkedDateField: 'corporateGovernanceCheckedDate',
      checkedByNameField: 'corporateGovernanceCheckedByUserName',
      finishedField: 'isCorporateGovernanceFinished',
      finishedDateField: 'corporateGovernanceFinishedDate',
      finishedByNameField: 'corporateGovernanceFinishedByUserName'
    },
    {
      key: 'csr',
      label: 'ESG',
      category: Category.Csr,
      checkedField: 'isCsrChecked',
      checkedDateField: 'csrCheckedDate',
      checkedByNameField: 'csrCheckedByUserName',
      finishedField: 'isCsrFinished',
      finishedDateField: 'csrFinishedDate',
      finishedByNameField: 'csrFinishedByUserName'
    },
    {
      key: 'votingResults',
      label: 'Voting Results',
      category: Category.VotingResults,
      checkedField: 'isVotingResultsChecked',
      checkedDateField: 'votingResultsCheckedDate',
      checkedByNameField: 'votingResultsCheckedByUserName',
      finishedField: 'isVotingResultsFinished',
      finishedDateField: 'votingResultsFinishedDate',
      finishedByNameField: 'votingResultsFinishedByUserName'
    },
    {
      key: 'compensation',
      label: 'Compensation',
      category: Category.Compensation,
      checkedField: 'isCompensationChecked',
      checkedDateField: 'compensationCheckedDate',
      checkedByNameField: 'compensationCheckedByUserName',
      finishedField: 'isCompensationFinished',
      finishedDateField: 'compensationFinishedDate',
      finishedByNameField: 'compensationFinishedByUserName'
    }
  ];

  form: FormGroup = this.fb.group({
    isAuditChecked: [false],
    auditCheckedDate: [null as string | null],
    auditCheckedByUserId: [null as string | null],
    isAuditFinished: [false],
    auditFinishedDate: [null as string | null],
    auditFinishedByUserId: [null as string | null],
    isCorporateGovernanceChecked: [false],
    corporateGovernanceCheckedDate: [null as string | null],
    corporateGovernanceCheckedByUserId: [null as string | null],
    isCorporateGovernanceFinished: [false],
    corporateGovernanceFinishedDate: [null as string | null],
    corporateGovernanceFinishedByUserId: [null as string | null],
    isCsrChecked: [false],
    csrCheckedDate: [null as string | null],
    csrCheckedByUserId: [null as string | null],
    isCsrFinished: [false],
    csrFinishedDate: [null as string | null],
    csrFinishedByUserId: [null as string | null],
    isVotingResultsChecked: [false],
    votingResultsCheckedDate: [null as string | null],
    votingResultsCheckedByUserId: [null as string | null],
    isVotingResultsFinished: [false],
    votingResultsFinishedDate: [null as string | null],
    votingResultsFinishedByUserId: [null as string | null],
    isCompensationChecked: [false],
    compensationCheckedDate: [null as string | null],
    compensationCheckedByUserId: [null as string | null],
    isCompensationFinished: [false],
    compensationFinishedDate: [null as string | null],
    compensationFinishedByUserId: [null as string | null],
    isAccountingChecked: [false],
    accountingCheckedDate: [null as string | null],
    accountingCheckedByUserId: [null as string | null],
    isAccountingFinished: [false],
    accountingFinishedDate: [null as string | null],
    accountingFinishedByUserId: [null as string | null]
  });

  ngOnInit(): void {
    this.companyId = this.route.snapshot.paramMap.get('id') ?? null;

    const user = this.currentUserState.user();
    this.isUserRole = user?.role === AppUserRole.User;
    this.isLeaderRole = user?.role === AppUserRole.Leader;

    if ((this.isUserRole || this.isLeaderRole) && user) {
      const effectiveCategory = user.category === Category.Accounting ? Category.Audit : user.category;
      this.visibleDomains = this.domains.filter(d => d.category === effectiveCategory);
    } else {
      this.visibleDomains = this.domains;
    }

    const storedYear = this.store.selectSignal(selectTabYear('process'))();
    const defaultYear = this.store.selectSignal(selectDefaultYear)();
    if (storedYear) {
      this.selectedYear = storedYear;
    } else if (defaultYear) {
      this.selectedYear = defaultYear;
      this.store.dispatch(setTabYear({ tabId: 'process', year: defaultYear }));
    } else {
      this.store.dispatch(setTabYear({ tabId: 'process', year: this.selectedYear }));
    }

    this.loadData();
  }

  onYearChange(year: string): void {
    if (this.hasChanges) {
      this.confirmationDialog.openConfirmationDialog(
        'Unsaved Changes',
        'You have unsaved changes. Are you sure you want to switch measurement?',
        'Switch',
        'Stay',
        'swap_horiz',
        'edit'
      ).subscribe(confirmed => {
        if (confirmed) {
          this.applyYearChange(year);
        } else {
          const current = this.selectedYear;
          this.selectedYear = '';
          setTimeout(() => this.selectedYear = current);
        }
      });
    } else {
      this.applyYearChange(year);
    }
  }

  private applyYearChange(year: string): void {
    this.selectedYear = year;
    this.store.dispatch(setTabYear({ tabId: 'process', year }));
    this.loadData();
  }

  onSave(): void {
    if (!this.companyId) return;
    this.performSave();
  }

  private performSave(): void {
    if (!this.companyId) return;

    if ((this.isUserRole || this.isLeaderRole) && this.visibleDomains.length === 1) {
      const domain = this.visibleDomains[0];
      const request: ProcessIndicationStatusUpdateRequest = {
        year: parseInt(this.selectedYear),
        category: domain.category,
        isFinished: this.form.get(domain.finishedField)?.value ?? false,
        isChecked: this.isLeaderRole ? (this.form.get(domain.checkedField)?.value ?? false) : null
      };

      this.spinnerService.show();
      this.processIndicationService.updateStatus(this.companyId, request).subscribe({
        next: (data) => {
          this.showYearWarning = false;
          this.patchForm(data);
          this.spinnerService.hide();
          this.toast.success('Status updated successfully');
          this.saved.emit();
        },
        error: (err) => {
          this.spinnerService.hide();
          this.toast.error('Failed to update status');
          console.error('Save error:', err);
        }
      });
      return;
    }

    const v = this.form.value;
    const request: UpsertProcessIndicationRequest = {
      year: parseInt(this.selectedYear),
      isAuditChecked: v.isAuditChecked,
      auditCheckedDate: v.auditCheckedDate,
      auditCheckedByUserId: v.auditCheckedByUserId,
      isAuditFinished: v.isAuditFinished,
      auditFinishedDate: v.auditFinishedDate,
      auditFinishedByUserId: v.auditFinishedByUserId,
      isCorporateGovernanceChecked: v.isCorporateGovernanceChecked,
      corporateGovernanceCheckedDate: v.corporateGovernanceCheckedDate,
      corporateGovernanceCheckedByUserId: v.corporateGovernanceCheckedByUserId,
      isCorporateGovernanceFinished: v.isCorporateGovernanceFinished,
      corporateGovernanceFinishedDate: v.corporateGovernanceFinishedDate,
      corporateGovernanceFinishedByUserId: v.corporateGovernanceFinishedByUserId,
      isCsrChecked: v.isCsrChecked,
      csrCheckedDate: v.csrCheckedDate,
      csrCheckedByUserId: v.csrCheckedByUserId,
      isCsrFinished: v.isCsrFinished,
      csrFinishedDate: v.csrFinishedDate,
      csrFinishedByUserId: v.csrFinishedByUserId,
      isVotingResultsChecked: v.isVotingResultsChecked,
      votingResultsCheckedDate: v.votingResultsCheckedDate,
      votingResultsCheckedByUserId: v.votingResultsCheckedByUserId,
      isVotingResultsFinished: v.isVotingResultsFinished,
      votingResultsFinishedDate: v.votingResultsFinishedDate,
      votingResultsFinishedByUserId: v.votingResultsFinishedByUserId,
      isCompensationChecked: v.isCompensationChecked,
      compensationCheckedDate: v.compensationCheckedDate,
      compensationCheckedByUserId: v.compensationCheckedByUserId,
      isCompensationFinished: v.isCompensationFinished,
      compensationFinishedDate: v.compensationFinishedDate,
      compensationFinishedByUserId: v.compensationFinishedByUserId,
      isAccountingChecked: v.isAccountingChecked,
      accountingCheckedDate: v.accountingCheckedDate,
      accountingCheckedByUserId: v.accountingCheckedByUserId,
      isAccountingFinished: v.isAccountingFinished,
      accountingFinishedDate: v.accountingFinishedDate,
      accountingFinishedByUserId: v.accountingFinishedByUserId
    };

    this.spinnerService.show();
    this.processIndicationService.upsert(this.companyId, request).subscribe({
      next: (data) => {
        this.showYearWarning = false;
        this.patchForm(data);
        this.spinnerService.hide();
        this.toast.success('Process indications saved successfully');
        this.saved.emit();
      },
      error: (err) => {
        this.spinnerService.hide();
        this.toast.error('Failed to save process indications');
        console.error('Save error:', err);
      }
    });
  }

  get hasChanges(): boolean {
    return this.form.dirty;
  }

  hasUnsavedChanges(): boolean {
    return this.hasChanges;
  }

  private loadData(): void {
    if (!this.companyId) return;

    this.spinnerService.show();
    this.processIndicationService.get(this.companyId, parseInt(this.selectedYear)).subscribe({
      next: resource => {
        if (resource.state === 'present' && resource.data) {
          this.showYearWarning = false;
          this.patchForm(resource.data);
        } else {
          this.resetForm();
          this.showYearWarning = resource.state === 'absent';
        }
        this.spinnerService.hide();
      },
      error: (error: any) => {
        this.spinnerService.hide();
        const apiError = error.error as ApiErrorResponse;

        if (apiError?.errorCode === ErrorCodes.NFO_ENTITY_NOT_FOUND) {
          // Entity not found - silently handle
        } else {
          this.toast.error('Error loading process indications', {
            iconTheme: {
              primary: 'var(--mat-sys-error)',
              secondary: '#FFF',
            }
          });
        }

        console.error('Process indication error:', error);
      }
    });
  }

  private resetForm(): void {
    this.form.reset({
      isAuditChecked: false, auditCheckedDate: null, auditCheckedByUserId: null,
      isAuditFinished: false, auditFinishedDate: null, auditFinishedByUserId: null,
      isCorporateGovernanceChecked: false, corporateGovernanceCheckedDate: null, corporateGovernanceCheckedByUserId: null,
      isCorporateGovernanceFinished: false, corporateGovernanceFinishedDate: null, corporateGovernanceFinishedByUserId: null,
      isCsrChecked: false, csrCheckedDate: null, csrCheckedByUserId: null,
      isCsrFinished: false, csrFinishedDate: null, csrFinishedByUserId: null,
      isVotingResultsChecked: false, votingResultsCheckedDate: null, votingResultsCheckedByUserId: null,
      isVotingResultsFinished: false, votingResultsFinishedDate: null, votingResultsFinishedByUserId: null,
      isCompensationChecked: false, compensationCheckedDate: null, compensationCheckedByUserId: null,
      isCompensationFinished: false, compensationFinishedDate: null, compensationFinishedByUserId: null,
      isAccountingChecked: false, accountingCheckedDate: null, accountingCheckedByUserId: null,
      isAccountingFinished: false, accountingFinishedDate: null, accountingFinishedByUserId: null
    }, { emitEvent: false });
    this.userNames = {};
    this.form.markAsPristine();
  }

  private patchForm(data: CompanyProcessIndicationDto): void {
    this.form.patchValue({
      isAuditChecked: data.isAuditChecked,
      auditCheckedDate: data.auditCheckedDate,
      auditCheckedByUserId: data.auditCheckedByUserId,
      isAuditFinished: data.isAuditFinished,
      auditFinishedDate: data.auditFinishedDate,
      auditFinishedByUserId: data.auditFinishedByUserId,
      isCorporateGovernanceChecked: data.isCorporateGovernanceChecked,
      corporateGovernanceCheckedDate: data.corporateGovernanceCheckedDate,
      corporateGovernanceCheckedByUserId: data.corporateGovernanceCheckedByUserId,
      isCorporateGovernanceFinished: data.isCorporateGovernanceFinished,
      corporateGovernanceFinishedDate: data.corporateGovernanceFinishedDate,
      corporateGovernanceFinishedByUserId: data.corporateGovernanceFinishedByUserId,
      isCsrChecked: data.isCsrChecked,
      csrCheckedDate: data.csrCheckedDate,
      csrCheckedByUserId: data.csrCheckedByUserId,
      isCsrFinished: data.isCsrFinished,
      csrFinishedDate: data.csrFinishedDate,
      csrFinishedByUserId: data.csrFinishedByUserId,
      isVotingResultsChecked: data.isVotingResultsChecked,
      votingResultsCheckedDate: data.votingResultsCheckedDate,
      votingResultsCheckedByUserId: data.votingResultsCheckedByUserId,
      isVotingResultsFinished: data.isVotingResultsFinished,
      votingResultsFinishedDate: data.votingResultsFinishedDate,
      votingResultsFinishedByUserId: data.votingResultsFinishedByUserId,
      isCompensationChecked: data.isCompensationChecked,
      compensationCheckedDate: data.compensationCheckedDate,
      compensationCheckedByUserId: data.compensationCheckedByUserId,
      isCompensationFinished: data.isCompensationFinished,
      compensationFinishedDate: data.compensationFinishedDate,
      compensationFinishedByUserId: data.compensationFinishedByUserId,
      isAccountingChecked: data.isAccountingChecked,
      accountingCheckedDate: data.accountingCheckedDate,
      accountingCheckedByUserId: data.accountingCheckedByUserId,
      isAccountingFinished: data.isAccountingFinished,
      accountingFinishedDate: data.accountingFinishedDate,
      accountingFinishedByUserId: data.accountingFinishedByUserId
    }, { emitEvent: false });

    this.userNames = {
      auditCheckedByUserName: data.auditCheckedByUserName,
      auditFinishedByUserName: data.auditFinishedByUserName,
      corporateGovernanceCheckedByUserName: data.corporateGovernanceCheckedByUserName,
      corporateGovernanceFinishedByUserName: data.corporateGovernanceFinishedByUserName,
      csrCheckedByUserName: data.csrCheckedByUserName,
      csrFinishedByUserName: data.csrFinishedByUserName,
      votingResultsCheckedByUserName: data.votingResultsCheckedByUserName,
      votingResultsFinishedByUserName: data.votingResultsFinishedByUserName,
      compensationCheckedByUserName: data.compensationCheckedByUserName,
      compensationFinishedByUserName: data.compensationFinishedByUserName,
      accountingCheckedByUserName: data.accountingCheckedByUserName,
      accountingFinishedByUserName: data.accountingFinishedByUserName
    };

    this.form.markAsPristine();
  }

  formatDate(dateStr: string | null): string {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }
}
